// Oyun çerçevesi sitenin kendi sayfasına döndü mü?
//
// Sağlayıcılara verilen dönüş/lobi adresi (Gold Palace, OroPlay, ScorpioPlay,
// Flexrix Casino ve Orjinal Casino'da `return_url`) sitenin kendi sayfasıdır.
// Oyuncu oyun içindeki "ana sayfa/lobi" düğmesine bastığında sağlayıcı
// çerçeveyi o adrese götürür ve pencerenin içinde **sitenin kendisi** açılırdı:
// site içinde site, üstelik oyun oturumu kapanmadan. Çerçeve bu adrese
// döndüğünde oyun bitmiştir; pencere kapatılır.
//
// Ölçü okunabilirliktir: sağlayıcının sayfası başka bir origin'dedir ve
// adresini okumak tarayıcıda hata verir; okunabiliyorsa çerçeve bizim
// origin'imizdedir. İki istisna vardır ve ikisi de oyunun kendisidir:
// `about:blank` (çerçeve henüz yüklenmedi) ve `/gamelaunch/` (oyun gateway'i
// oyunu ana siteyle aynı origin'den sunar — bkz. services/game_gateway.py).
const GATEWAY_PREFIX = '/gamelaunch/';

export const frameReturnedToSite = (frame, origin = window.location.origin) => {
  let href = '';
  try {
    href = frame?.contentWindow?.location?.href || '';
  } catch {
    // Başka origin: çerçeve hâlâ sağlayıcının sayfasında.
    return false;
  }
  if (!href || href === 'about:blank') return false;
  let parsed;
  try {
    parsed = new URL(href);
  } catch {
    return false;
  }
  if (parsed.origin !== origin) return false;
  return !parsed.pathname.startsWith(GATEWAY_PREFIX);
};

// Oyun içi lobi kapısı.
//
// Oyunların çoğunda sağlayıcının kendi "lobi / ana sayfa" düğmesi vardır ve
// çerçeveyi sağlayıcının (ya da agregatörün) oyun listesine götürür. Oradan
// aynı oturumla başka bir oyun açılır ve o oyun bizim vitrinimizden hiç
// geçmez. Lobi sayfası başka bir origin'dedir: adresini okuyamayız, sayfasına
// dokunamayız. Okuyabildiğimiz tek şey çerçevenin **yeniden yüklendiğidir**:
// oyun bir kez açıldıktan sonra gelen her yükleme, çerçevenin oyundan başka
// bir sayfaya geçtiği demektir. Karşılığı oyunun yeniden açılmasıdır — oyuncu
// lobiyi değil, açtığı oyunu görür.
//
// Üç sınır bilinçlidir:
// * **El sıkışma penceresi.** Bazı launch adresleri oyunu betikle bir iki
//   yönlendirmeden sonra açar; ilk yüklemeden sonraki `HANDSHAKE_MS` içinde
//   gelen yükleme açılışın parçasıdır.
// * **Döngü kesici.** Oyun yeniden açıldıktan hemen sonra (`MIN_RELAUNCH_MS`)
//   çerçeve yine başka sayfaya geçerse sağlayıcı kendini yeniliyordur; sonsuz
//   yeniden açma durdurulur ve çerçeve **olduğu gibi bırakılır**. Bir süre
//   orada pencere kapatılıyordu ve şikâyet "oyundan atıyor" diye geliyordu:
//   kapı yanlış tetiklendiğinde (sağlayıcının kendi yenilemesi, yavaş
//   bağlantıda geç gelen yönlendirme) oyuncu turun ortasında dışarı düşüyordu.
//   Kapının işi parayı korumak **değildir** — kapalı oyuna gelen bahsi cüzdan
//   webhook'u reddeder (bkz. services/game_play_guard.py); burada yalnızca
//   lobi ekranının yerine oyunun gelmesi amaçlanır. En kötü ihtimalle oyuncu
//   lobiyi görür, oradan açtığı kapalı oyun yine oynanamaz.
// * **Canlı casino kapsam dışıdır.** Orada lobi oyunun kendisidir (masa
//   seçimi); kapı uygulansaydı oyuncu hiçbir masaya oturamazdı.
// * **Lobi oyunu kapsam dışıdır.** Katalogda adı "Lobby"/"Lobi" olan kayıtlar
//   (Evolution Lobby, Pragmatic Live Lobby …) oyun değil sağlayıcının kendi
//   seçim ekranıdır; sağlayıcı onları her zaman canlı casino kategorisinde
//   bildirmiyor. Kapı orada seçilen her masayı "lobiye geçti" sayıp oyunu
//   baştan açıyor, ikinci seçimde de pencereyi kapatıyordu — lobi oyunları hiç
//   oynanamıyordu.
// * **Demo kapsam dışıdır.** Kapının amacı kapatılmış bir oyunun lobiden
//   **gerçek parayla** oynanmasını önlemektir; demoda para yoktur. Bazı demo
//   adresleri oyunu açmadan önce birkaç kez yönlendiriyor ve yavaş bağlantıda
//   bu yönlendirmeler el sıkışma penceresinin dışına düşüyordu: demo "lobiye
//   geçti" sayılıp yeniden açılıyor, ardından pencere kapatılıyordu.
// El sıkışma penceresi bilinçli olarak geniştir: mobil bağlantıda sağlayıcının
// açılış yönlendirmeleri sekiz saniyeyi aşabiliyor ve o yüklemeler "lobiye
// geçildi" sayılınca oyun daha açılmadan yeniden başlatılıyordu.
export const HANDSHAKE_MS = 15000;
export const MIN_RELAUNCH_MS = 15000;
const LIVE_CASINO = 'live-casino';
const LOBBY_NAME = /(^|[^a-z0-9ğüşöçıi])(lobby|lobi)([^a-z0-9ğüşöçıi]|$)/i;

export const isLobbyGame = (game) => LOBBY_NAME.test(String(game?.name || ''));

// Lobi davranışı panelden seçilir (**Oyun Kontrol → Gateway & Genel**, bkz.
// backend/services/game_lobby.py). Üç değer vardır:
//
// * `relaunch` — varsayılan ve yukarıda anlatılan davranış: oyun yeniden açılır.
// * `site` — oyun kapanır ve oyuncu **sitenin kendi** oyun listesine döner.
//   "Lobi düğmesi hiçbir şey yapmıyor" şikâyetinin cevabı budur: düğme artık
//   bir yere götürür, ama bizim vitrinimize.
// * `provider` — kapı hiç uygulanmaz, sağlayıcının lobisi çerçevede açılır.
export const LOBBY_RELAUNCH = 'relaunch';
export const LOBBY_SITE = 'site';
export const LOBBY_PROVIDER = 'provider';

export const normalizeLobbyMode = (value) => (
  [LOBBY_RELAUNCH, LOBBY_SITE, LOBBY_PROVIDER].includes(value)
    ? value : LOBBY_RELAUNCH
);

export const frameGuardEnabled = (game, mode = 'real', lobbyMode = LOBBY_RELAUNCH) => {
  if (normalizeLobbyMode(lobbyMode) === LOBBY_PROVIDER) return false;
  if (mode === 'demo') return false;
  if (game?.live) return false;
  if (String(game?.category || '').trim().toLowerCase() === LIVE_CASINO) return false;
  return !isLobbyGame(game);
};

// Bir yükleme olayının ne anlama geldiği: 'first' (oyun açıldı),
// 'handshake' (açılışın parçası), 'navigated' (oyundan çıkıldı → yeniden aç),
// 'loop' (yeniden açılan oyun da hemen çıktı → bırak, pencereyi kapatma).
export const classifyFrameLoad = ({ now, firstLoadAt, lastRelaunchAt }) => {
  if (firstLoadAt == null) return 'first';
  if (now - firstLoadAt < HANDSHAKE_MS) return 'handshake';
  if (lastRelaunchAt != null && now - lastRelaunchAt < MIN_RELAUNCH_MS) return 'loop';
  return 'navigated';
};

export default frameReturnedToSite;
